/**
 * The Templates WYSIWYG editor — TipTap with a flat, hairline toolbar.
 *
 * Merge tokens ({{first_name}} …) live in the document as inline atom nodes
 * rendered as small accent pills, but everything that leaves this component
 * (onChange, and what the screen saves) is plain HTML with literal {{token}}
 * text — the renderer and the Worker never learn pills existed.
 */
import React from 'react'
import { EditorContent, useEditor, type Editor } from '@tiptap/react'
import { Extension, Node, mergeAttributes } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import { groupMergeFields } from '@shared/render'
import { CUSTOM_FIELD_OPTION, promptForCustomField } from './HtmlEditor'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextStyle from '@tiptap/extension-text-style'
import { Color } from '@tiptap/extension-color'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import Image from '@tiptap/extension-image'
import { Ic } from './icons'
import { buildCtaHtml } from '@shared/cta'

// --- serialization: pills <-> {{tokens}} --------------------------------------

/** Storage/editor boundary: literal {{key}} text -> pill nodes. */
export function tokensToPills(html: string): string {
  return (html ?? '').replace(/\{\{\s*(\w+)\s*\}\}/g, '<span data-merge="$1"></span>')
}

/** Editor -> storage: pill nodes back to literal {{key}} text. */
export function pillsToTokens(html: string): string {
  return (html ?? '').replace(/<span[^>]*data-merge="(\w+)"[^>]*>[\s\S]*?<\/span>/g, '{{$1}}')
}

// --- custom extensions --------------------------------------------------------

const PILL_STYLE =
  'display:inline-block;padding:1px 8px;border-radius:6px;background:var(--accent-soft);' +
  'border:1px solid var(--accent-soft-border);color:var(--accent-text);font-size:13px;' +
  'font-weight:600;line-height:1.5;white-space:nowrap;'

/** Inline atom that shows {{key}} as an accent pill. */
const MergeToken = Node.create({
  name: 'mergeToken',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      k: {
        default: '',
        parseHTML: (el: HTMLElement) => el.getAttribute('data-merge') ?? '',
      },
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-merge]' }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-merge': node.attrs.k, style: PILL_STYLE }),
      `{{${node.attrs.k}}}`,
    ]
  },

  renderText({ node }) {
    return `{{${node.attrs.k}}}`
  },
})

/** font-size + font-family on the shared textStyle mark (no official v2 exts). */
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (el: HTMLElement) => el.style.fontSize || null,
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.fontSize ? { style: `font-size:${attrs.fontSize}` } : {},
          },
          fontFamily: {
            default: null,
            parseHTML: (el: HTMLElement) => el.style.fontFamily || null,
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.fontFamily ? { style: `font-family:${attrs.fontFamily}` } : {},
          },
          // Highlight rides the existing textStyle mark rather than pulling in
          // @tiptap/extension-highlight — one fewer dependency, and it
          // serialises to a plain inline background-color, which is what an
          // email client will actually honour.
          backgroundColor: {
            default: null,
            parseHTML: (el: HTMLElement) => el.style.backgroundColor || null,
            renderHTML: (attrs: Record<string, unknown>) =>
              attrs.backgroundColor
                ? { style: `background-color:${attrs.backgroundColor}` }
                : {},
          },
        },
      },
    ]
  },
})

const FONT_SIZES = ['12px', '13px', '14px', '15px', '16px', '18px', '22px']

/** email-safe stacks only */
const FONT_FAMILIES = [
  { label: 'Sans', value: '' },
  { label: 'Serif', value: 'Georgia, serif' },
  { label: 'Mono', value: 'monospace' },
]

const ALIGN_CYCLE = ['left', 'center', 'right'] as const

/** What the inline insert row is currently collecting. */
type InsertKind = 'link' | 'image' | 'cta'

const insertFieldStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 28,
  padding: '0 8px',
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
  color: 'var(--text-primary)',
  background: 'var(--surface-card)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  outline: 'none',
}

// --- component -----------------------------------------------------------------

export interface RichEditorProps {
  /** HTML with {{tokens}} — converted to pills internally */
  initialHtml: string
  placeholder?: string
  /** token keys offered by the "Insert field" dropdown */
  mergeFields: string[]
  /** fires with serialized HTML (pills already back to {{tokens}}) */
  onChange: (html: string) => void
}

export interface RichEditorParts {
  /** formatting controls — the split editor mounts these in the pane header */
  toolbar: React.ReactNode
  /** the link/image URL row, only present while one is being inserted */
  insertRow: React.ReactNode
  /** the editable ProseMirror surface */
  surface: React.ReactNode
}

/**
 * Returns the editor's pieces separately so a caller can place the toolbar and
 * the editable surface in different containers (the split layout puts the
 * toolbar in the left pane's 38px header strip). Everything stays wired to one
 * editor instance.
 */
export function useRichEditor({
  initialHtml,
  placeholder,
  mergeFields,
  onChange,
}: RichEditorProps): RichEditorParts {
  // which inline input row is open under the toolbar
  const [insert, setInsert] = React.useState<InsertKind | null>(null)
  const [insertValue, setInsertValue] = React.useState('')
  /** CTA only: the button's visible text. */
  const [insertLabel, setInsertLabel] = React.useState('')

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, autolink: true }),
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({ types: ['paragraph', 'heading'] }),
      Placeholder.configure({ placeholder: placeholder ?? 'Write your email…' }),
      Image.configure({ inline: false }),
      MergeToken,
    ],
    content: tokensToPills(initialHtml),
    onUpdate: ({ editor: e }) => onChange(pillsToTokens(e.getHTML())),
  })

  if (!editor) return { toolbar: null, insertRow: null, surface: null }

  /**
   * Link ALWAYS opens the dialog, prefilled with the current href.
   *
   * It used to unlink when a link was already active, which meant an existing
   * link could be destroyed but never edited — there was no path to "change
   * this URL". Removal is now its own button, so each does exactly one thing.
   */
  function openInsert(kind: InsertKind) {
    if (!editor) return
    setInsert(kind)
    setInsertLabel(kind === 'cta' ? (editor.state.selection.empty ? '' : selectedText()) : '')
    setInsertValue(kind === 'link' ? (editor.getAttributes('link').href ?? '') : '')
  }

  function selectedText(): string {
    if (!editor) return ''
    const { from, to } = editor.state.selection
    return editor.state.doc.textBetween(from, to, ' ').trim()
  }

  function applyInsert() {
    if (!editor || !insert) return
    const url = insertValue.trim()

    if (insert === 'cta') {
      // Inserted as raw HTML: the table markup IS the payload, and it must
      // survive a round-trip through the HTML source view unchanged.
      editor
        .chain()
        .focus()
        .insertContent(buildCtaHtml({ label: insertLabel.trim() || 'Click here', url }))
        .run()
    } else if (url) {
      if (insert === 'link') {
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
      } else {
        editor.chain().focus().setImage({ src: url }).run()
      }
    }

    setInsert(null)
    setInsertValue('')
    setInsertLabel('')
  }


  const currentSize = (editor.getAttributes('textStyle').fontSize as string) || '15px'
  const currentFamily = (editor.getAttributes('textStyle').fontFamily as string) || ''
  const align =
    ALIGN_CYCLE.find((a) => editor.isActive({ textAlign: a })) ?? 'left'

  const heading = editor.isActive('heading', { level: 2 })
  const hl = (editor.getAttributes('textStyle').backgroundColor as string) || ''

  const toolbar = (
    <div className="rte-bar">
      {/* history */}
      <div className="rte-group">
        <Key label="Undo" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
          <Ic.undo size={15} />
        </Key>
        <Key label="Redo" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
          <Ic.redo size={15} />
        </Key>
      </div>

      <span className="rte-div" />

      {/* type */}
      <div className="rte-group">
        <select
          className="rte-key"
          title="Font"
          style={{ width: 74 }}
          value={FONT_FAMILIES.some((f) => f.value === currentFamily) ? currentFamily : ''}
          onChange={(e) =>
            editor.chain().focus().setMark('textStyle', { fontFamily: e.target.value || null }).run()
          }
        >
          {FONT_FAMILIES.map((f) => (
            <option key={f.label} value={f.value}>{f.label}</option>
          ))}
        </select>
        <select
          className="rte-key"
          title="Font size"
          style={{ width: 58 }}
          value={FONT_SIZES.includes(currentSize) ? currentSize : '15px'}
          onChange={(e) => editor.chain().focus().setMark('textStyle', { fontSize: e.target.value }).run()}
        >
          {FONT_SIZES.map((s) => (
            <option key={s} value={s}>{s.replace('px', '')}</option>
          ))}
        </select>
      </div>

      <span className="rte-div" />

      {/* marks */}
      <div className="rte-group">
        <Key label="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <Ic.bold size={15} />
        </Key>
        <Key label="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <Ic.italic size={15} />
        </Key>
        <Key label="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}>
          <Ic.underline size={15} />
        </Key>
        <Key label="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <Ic.strike size={15} />
        </Key>
      </div>

      <span className="rte-div" />

      {/* colour */}
      <div className="rte-group">
        <ColorKey
          label="Text color"
          value={(editor.getAttributes('textStyle').color as string) || '#141620'}
          swatch={(editor.getAttributes('textStyle').color as string) || undefined}
          onChange={(v) => editor.chain().focus().setColor(v).run()}
        >
          <Ic.textColor size={15} />
        </ColorKey>
        <ColorKey
          label="Highlight"
          value={hl || '#fff3a3'}
          swatch={hl || undefined}
          active={!!hl}
          onChange={(v) => editor.chain().focus().setMark('textStyle', { backgroundColor: v }).run()}
        >
          <Ic.highlight size={15} />
        </ColorKey>
      </div>

      <span className="rte-div" />

      {/* INSERT — link gets room of its own. This is the group that used to be
          a cramped 26px button wedged between underline and image. */}
      <div className="rte-group">
        <Key label="Insert link" active={editor.isActive('link')} onClick={() => openInsert('link')}>
          <Ic.link size={15} />
        </Key>
        <Key
          label="Remove link"
          disabled={!editor.isActive('link')}
          onClick={() => editor.chain().focus().extendMarkRange('link').unsetLink().run()}
        >
          <Ic.unlink size={15} />
        </Key>
        <Key label="Insert image" onClick={() => openInsert('image')}>
          <Ic.image size={15} />
        </Key>
        <Key label="Insert button" onClick={() => openInsert('cta')}>
          <Ic.cta size={15} />
        </Key>
      </div>

      <span className="rte-div" />

      {/* blocks */}
      <div className="rte-group">
        <Key label="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <Ic.listUl size={15} />
        </Key>
        <Key label="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <Ic.listOl size={15} />
        </Key>
        <Key label="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <Ic.quote size={15} />
        </Key>
      </div>

      <span className="rte-div" />

      {/* alignment — three explicit buttons, not one cycling button: a cycle
          cannot show which alignment is currently on. */}
      <div className="rte-group">
        <Key label="Align left" active={align === 'left'} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <Ic.alignLeft size={15} />
        </Key>
        <Key label="Align center" active={align === 'center'} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <Ic.alignCenter size={15} />
        </Key>
        <Key label="Align right" active={align === 'right'} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <Ic.alignRight size={15} />
        </Key>
      </div>

      <span className="rte-div" />

      {/* structure */}
      <div className="rte-group">
        <Key label="Heading" active={heading} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
          <Ic.heading size={15} />
        </Key>
        <Key label="Horizontal rule" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Ic.hr size={15} />
        </Key>
        <Key label="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
          <Ic.clearFormat size={15} />
        </Key>
      </div>

      <span className="rte-div" />

      {/* merge fields */}
      <div className="rte-group">
        <label className="rte-key is-accent" title="Insert field" style={{ position: 'relative' }}>
          <Ic.braces size={14} />
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>Insert field</span>
          <Ic.chevronDown size={12} />
          <select
            value=""
            onChange={(e) => {
              const raw = e.target.value
              const k = raw === CUSTOM_FIELD_OPTION ? promptForCustomField() : raw
              if (k) editor.chain().focus().insertContent({ type: 'mergeToken', attrs: { k } }).run()
            }}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
          >
            <option value="" disabled>Insert field</option>
            {groupMergeFields(mergeFields).map((group) => (
              <optgroup key={group.label} label={group.label}>
                {group.tokens.map((k) => (
                  <option key={k} value={k}>{`{{${k}}}`}</option>
                ))}
              </optgroup>
            ))}
            <option value={CUSTOM_FIELD_OPTION}>＋ Add custom field…</option>
          </select>
        </label>
      </div>
    </div>
  )

  const insertRow = insert ? (
    <div
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        padding: '6px 8px',
        border: '1px solid var(--border-default)',
        borderRadius: 6,
        background: 'var(--surface-sunken)',
        marginBottom: 8,
      }}
    >
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 'none' }}>
            {insert === 'link' ? 'Link URL' : insert === 'image' ? 'Image URL' : 'Button'}
          </span>
          {/* CTA needs a second field — the label the recipient actually reads. */}
          {insert === 'cta' && (
            <input
              autoFocus
              value={insertLabel}
              onChange={(e) => setInsertLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') applyInsert()
                if (e.key === 'Escape') setInsert(null)
              }}
              placeholder="Button text"
              style={{ ...insertFieldStyle, flex: '0 1 170px' }}
            />
          )}
          <input
            autoFocus={insert !== 'cta'}
            value={insertValue}
            onChange={(e) => setInsertValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applyInsert()
              if (e.key === 'Escape') setInsert(null)
            }}
            placeholder="https://…"
            style={insertFieldStyle}
          />
      <MiniButton onClick={applyInsert}>Apply</MiniButton>
      <MiniButton subtle onClick={() => setInsert(null)}>
        Cancel
      </MiniButton>
    </div>
  ) : null

  return {
    toolbar,
    insertRow,
    surface: (
      <div className="rte rte-fill">
        <EditorContent editor={editor} />
      </div>
    ),
  }
}

// --- toolbar bits ---------------------------------------------------------------

/**
 * One toolbar key.
 *
 * onMouseDown is prevented so the editor keeps its selection — without it,
 * clicking Bold blurs the document first and the mark applies to nothing. The
 * raised/pressed look is all CSS (.rte-key), driven by aria-pressed, so the
 * toggled state is announced to assistive tech as well as drawn.
 */
function Key({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      className="rte-key"
      title={label}
      aria-label={label}
      aria-pressed={!!active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

/** A key whose face carries a colour swatch and opens the native picker. */
function ColorKey({
  label,
  value,
  swatch,
  active,
  onChange,
  children,
}: {
  label: string
  value: string
  swatch?: string
  active?: boolean
  onChange: (v: string) => void
  children: React.ReactNode
}) {
  return (
    <label
      className="rte-key"
      title={label}
      aria-label={label}
      aria-pressed={!!active}
      style={{ position: 'relative', flexDirection: 'column', gap: 0 }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {children}
      {/* the current colour, as a bar under the glyph */}
      <span
        aria-hidden="true"
        style={{
          width: 14,
          height: 3,
          borderRadius: 2,
          marginTop: 1,
          background: swatch || 'var(--border-strong, var(--border-default))',
        }}
      />
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
      />
    </label>
  )
}


function MiniButton({
  subtle,
  onClick,
  children,
}: {
  subtle?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 26,
        padding: '0 10px',
        fontSize: 12,
        fontWeight: 500,
        fontFamily: 'var(--font-sans)',
        border: subtle ? '1px solid transparent' : '1px solid var(--accent)',
        borderRadius: 'var(--radius-sm)',
        cursor: 'pointer',
        background: subtle ? 'transparent' : 'var(--accent)',
        color: subtle ? 'var(--text-secondary)' : 'var(--text-on-accent)',
      }}
    >
      {children}
    </button>
  )
}


export type { Editor }
